import React, { useState } from 'react';
import Layout from '@/components/layout/Layout';
import StatCard from '@/components/dashboard/StatCard';
import DateRangeFilter from '@/components/governance/DateRangeFilter';
import type { DateRange } from '@/components/governance/DateRangeFilter';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
    AlertTriangle,
    Factory,
    ShieldAlert,
    TrendingDown,
    TrendingUp,
    ArrowUpDown,
    CheckCircle2,
    Clock,
    XCircle,
    Wheat,
} from 'lucide-react';
import { subDays } from 'date-fns';
import {
    useSupplierGovernance,
    type SupplierGovernanceMetrics,
    type RiskLevel,
} from '@/hooks/use-supplier-governance';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (v: number): string => {
    if (v >= 1_000_000) return `Br ${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `Br ${(v / 1_000).toFixed(1)}K`;
    return `Br ${v.toFixed(0)}`;
};

const formatKg = (v: number): string => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M kg`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K kg`;
    return `${v.toFixed(0)} kg`;
};

const formatDate = (iso: string | null): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const riskBadge = (level: RiskLevel) => {
    const cfg = {
        high: { label: 'High Risk', className: 'bg-red-100 text-red-700 border-red-200' },
        medium: { label: 'Medium', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
        low: { label: 'Low Risk', className: 'bg-green-100 text-green-700 border-green-200' },
    }[level];
    return (
        <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cfg.className}`}
        >
            {cfg.label}
        </span>
    );
};

const rateColor = (rate: number, inverse = false): string => {
    const isGood = inverse ? rate >= 75 : rate <= 10;
    const isMid = inverse ? rate >= 50 : rate <= 25;
    if (isGood) return 'text-green-600';
    if (isMid) return 'text-yellow-600';
    return 'text-red-600';
};

// ─── Sort ─────────────────────────────────────────────────────────────────────

type SortKey = keyof Pick<
    SupplierGovernanceMetrics,
    | 'supplier_name'
    | 'risk_level'
    | 'rejection_rate'
    | 'completion_rate'
    | 'total_value'
    | 'total_procurements'
    | 'kg_contribution_pct'
    | 'approved_quantity_kg'
    | 'total_gross_damage_kg'
    | 'gross_damage_pct'
    | 'total_compensation_kg'
    | 'compensation_pct'
    | 'total_net_damage_kg'
    | 'net_damage_pct'
>;

const RISK_ORDER: Record<RiskLevel, number> = { high: 0, medium: 1, low: 2 };

const sortMetrics = (
    metrics: SupplierGovernanceMetrics[],
    field: SortKey,
    dir: 'asc' | 'desc',
) =>
    [...metrics].sort((a, b) => {
        let cmp = 0;
        if (field === 'risk_level') {
            cmp = RISK_ORDER[a.risk_level] - RISK_ORDER[b.risk_level];
        } else if (field === 'supplier_name') {
            cmp = a.supplier_name.localeCompare(b.supplier_name);
        } else {
            cmp = (a[field] as number) - (b[field] as number);
        }
        return dir === 'asc' ? cmp : -cmp;
    });

// ─── Sub-components ───────────────────────────────────────────────────────────

const TableSkeletonRows = ({ cols, rows = 5 }: { cols: number; rows?: number }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
                {Array.from({ length: cols }).map((__, j) => (
                    <TableCell key={j}>
                        <Skeleton className="h-4 w-full" />
                    </TableCell>
                ))}
            </TableRow>
        ))}
    </>
);

const KpiSkeleton = () => (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="dashboard-card p-4 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-24" />
            </div>
        ))}
    </div>
);

const SortTh = ({
    label,
    field,
    current,
    dir,
    onClick,
}: {
    label: string;
    field: SortKey;
    current: SortKey;
    dir: 'asc' | 'desc';
    onClick: (f: SortKey) => void;
}) => (
    <TableHead
        className="cursor-pointer select-none whitespace-nowrap"
        onClick={() => onClick(field)}
    >
        <span className="inline-flex items-center gap-1">
            {label}
            <ArrowUpDown
                className={`h-3 w-3 ${current === field ? 'text-primary' : 'text-muted-foreground'}`}
            />
        </span>
    </TableHead>
);

// ─── Default date range (last 90 days) ───────────────────────────────────────

const DEFAULT_RANGE: DateRange = {
    startDate: subDays(new Date(), 90),
    endDate: new Date(),
};

// ─── Main page ────────────────────────────────────────────────────────────────

const SupplierGovernanceDashboard: React.FC = () => {
    const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);

    const [riskSort, setRiskSort] = useState<SortKey>('risk_level');
    const [riskDir, setRiskDir] = useState<'asc' | 'desc'>('asc');
    const [perfSort, setPerfSort] = useState<SortKey>('approved_quantity_kg');
    const [perfDir, setPerfDir] = useState<'asc' | 'desc'>('desc');

    const { data, isLoading, isError } = useSupplierGovernance(dateRange);

    const handleSort = (
        field: SortKey,
        current: SortKey,
        setField: (f: SortKey) => void,
        dir: 'asc' | 'desc',
        setDir: (d: 'asc' | 'desc') => void,
    ) => {
        if (field === current) setDir(dir === 'asc' ? 'desc' : 'asc');
        else { setField(field); setDir('asc'); }
    };

    // ── Error state ──
    if (isError) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="text-center space-y-2">
                        <XCircle className="h-10 w-10 text-destructive mx-auto" />
                        <p className="text-lg font-semibold">Failed to load governance data</p>
                        <p className="text-sm text-muted-foreground">Check your connection and try refreshing.</p>
                    </div>
                </div>
            </Layout>
        );
    }

    const summary = data?.summary;
    const metrics = data?.supplierMetrics ?? [];
    const alerts = data?.riskAlerts ?? [];

    const activeMetrics = metrics.filter((m) => m.status === 'active');
    const sortedRisk = sortMetrics(activeMetrics, riskSort, riskDir);
    const sortedPerf = sortMetrics(metrics, perfSort, perfDir);

    const totalActive = summary?.total_active_suppliers ?? 0;
    const highRisk = summary?.high_risk_count ?? 0;
    const medRisk = summary?.medium_risk_count ?? 0;
    const lowRisk = summary?.low_risk_count ?? 0;

    return (
        <Layout>
            <div className="space-y-5">
                {/* ── Page header ── */}
                <div>
                    <h1 className="text-2xl font-bold tracking-tight">Supplier Governance Dashboard</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Read-only risk and compliance view — filtered to approved procurement records within the selected period.
                    </p>
                </div>

                {/* ── Date filter ── */}
                <DateRangeFilter value={dateRange} onChange={setDateRange} />

                {/* ── KPI row (8 cards) ── */}
                {isLoading ? (
                    <KpiSkeleton />
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
                        <StatCard
                            title="Active Suppliers"
                            value={String(summary?.total_active_suppliers ?? 0)}
                            icon={<Factory className="h-5 w-5 text-primary" />}
                        />
                        <StatCard
                            title="High Risk Suppliers"
                            value={String(highRisk)}
                            icon={<ShieldAlert className="h-5 w-5 text-red-500" />}
                            className={highRisk > 0 ? 'border-red-200 bg-red-50/40' : ''}
                        />
                        <StatCard
                            title="Approved Sourcing Value"
                            value={formatCurrency(summary?.total_sourcing_value ?? 0)}
                            icon={<TrendingUp className="h-5 w-5 text-primary" />}
                        />
                        <StatCard
                            title="Total Approved KG"
                            value={formatKg(summary?.total_approved_kg ?? 0)}
                            icon={<Wheat className="h-5 w-5 text-primary" />}
                        />
                        <StatCard
                            title="Avg Rejection Rate"
                            value={`${(summary?.avg_rejection_rate ?? 0).toFixed(1)}%`}
                            icon={<TrendingDown className="h-5 w-5 text-muted-foreground" />}
                            className={
                                (summary?.avg_rejection_rate ?? 0) > 15
                                    ? 'border-red-200 bg-red-50/40'
                                    : (summary?.avg_rejection_rate ?? 0) > 8
                                        ? 'border-yellow-200 bg-yellow-50/40'
                                        : ''
                            }
                        />
                        <StatCard
                            title="Avg Gross Damage %"
                            value={`${(summary?.avg_gross_damage_pct ?? 0).toFixed(2)}%`}
                            icon={<AlertTriangle className="h-5 w-5 text-orange-500" />}
                            className={
                                (summary?.avg_gross_damage_pct ?? 0) > 10
                                    ? 'border-red-200 bg-red-50/40'
                                    : (summary?.avg_gross_damage_pct ?? 0) > 5
                                        ? 'border-yellow-200 bg-yellow-50/40'
                                        : ''
                            }
                        />
                        <StatCard
                            title="Avg Compensation %"
                            value={`${(summary?.avg_compensation_pct ?? 0).toFixed(2)}%`}
                            icon={<TrendingUp className="h-5 w-5 text-green-500" />}
                            className="border-green-200 bg-green-50/40"
                        />
                        <StatCard
                            title="Avg Net Damage %"
                            value={`${(summary?.avg_net_damage_pct ?? 0).toFixed(2)}%`}
                            icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
                            className={
                                (summary?.avg_net_damage_pct ?? 0) > 10
                                    ? 'border-red-200 bg-red-50/40'
                                    : (summary?.avg_net_damage_pct ?? 0) > 5
                                        ? 'border-yellow-200 bg-yellow-50/40'
                                        : 'border-green-200 bg-green-50/40'
                            }
                        />
                    </div>
                )}

                {/* ── Risk distribution ── */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Risk Distribution — Active Suppliers</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <div className="space-y-3">
                                {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-4 w-full" />)}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {[
                                    { label: 'Low Risk', count: lowRisk, colorClass: 'bg-green-500', textColor: 'text-green-700' },
                                    { label: 'Medium Risk', count: medRisk, colorClass: 'bg-yellow-400', textColor: 'text-yellow-700' },
                                    { label: 'High Risk', count: highRisk, colorClass: 'bg-red-500', textColor: 'text-red-700' },
                                ].map(({ label, count, textColor }) => {
                                    const pct = totalActive > 0 ? (count / totalActive) * 100 : 0;
                                    return (
                                        <div key={label} className="flex items-center gap-4">
                                            <span className="w-24 text-sm text-muted-foreground shrink-0">{label}</span>
                                            <div className="flex-1">
                                                <Progress value={pct} className="h-2" />
                                            </div>
                                            <span className={`w-20 text-right text-sm font-semibold ${textColor}`}>
                                                {count} ({pct.toFixed(0)}%)
                                            </span>
                                        </div>
                                    );
                                })}
                                <p className="text-xs text-muted-foreground pt-1">
                                    Total active suppliers: <span className="font-medium">{totalActive}</span>
                                </p>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* ── Risk Alerts ── */}
                {(isLoading || alerts.length > 0) && (
                    <Card className={alerts.some((a) => a.severity === 'critical') ? 'border-red-200' : 'border-yellow-200'}>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                Risk Alerts
                                {!isLoading && (
                                    <Badge variant="secondary" className="ml-auto text-xs">
                                        {alerts.length}
                                    </Badge>
                                )}
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            {isLoading ? (
                                <div className="space-y-2">
                                    {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-md" />)}
                                </div>
                            ) : (
                                <div className="space-y-2 max-h-72 overflow-y-auto">
                                    {alerts.map((alert, idx) => (
                                        <div
                                            key={`${alert.supplier_id}-${alert.alert_type}-${idx}`}
                                            className={`flex items-start gap-3 rounded-md border px-3 py-2.5 text-sm ${alert.severity === 'critical'
                                                    ? 'border-red-200 bg-red-50'
                                                    : 'border-yellow-100 bg-yellow-50'
                                                }`}
                                        >
                                            <AlertTriangle
                                                className={`h-4 w-4 mt-0.5 shrink-0 ${alert.severity === 'critical' ? 'text-red-500' : 'text-yellow-500'
                                                    }`}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate">{alert.supplier_name}</p>
                                                <p className="text-muted-foreground text-xs mt-0.5">{alert.description}</p>
                                            </div>
                                            <span
                                                className={`shrink-0 rounded-full border px-2 py-0.5 text-xs font-semibold ${alert.severity === 'critical'
                                                        ? 'bg-red-100 text-red-700 border-red-200'
                                                        : 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                                    }`}
                                            >
                                                {alert.severity === 'critical' ? 'Critical' : 'Warning'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* ── Supplier Risk Table ── */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Supplier Risk Table</CardTitle>
                        <p className="text-xs text-muted-foreground">Active suppliers · Click column header to sort</p>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <SortTh label="Supplier" field="supplier_name" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                    <TableHead>Region</TableHead>
                                    <SortTh label="Orders" field="total_procurements" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                    <SortTh label="Rejection %" field="rejection_rate" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                    <SortTh label="Completion %" field="completion_rate" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                    <SortTh label="KG Contribution" field="kg_contribution_pct" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                    <SortTh label="Net Damage (kg)" field="total_net_damage_kg" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                    <SortTh label="Net Damage %" field="net_damage_pct" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                    <TableHead>Last Order</TableHead>
                                    <SortTh label="Risk Level" field="risk_level" current={riskSort} dir={riskDir}
                                        onClick={(f) => handleSort(f, riskSort, setRiskSort, riskDir, setRiskDir)} />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? <TableSkeletonRows cols={10} /> :
                                    sortedRisk.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={10} className="text-center text-muted-foreground py-10">
                                                No active supplier records in the selected date range.
                                            </TableCell>
                                        </TableRow>
                                    ) : sortedRisk.map((s) => (
                                        <TableRow key={s.supplier_id} className="hover:bg-muted/30">
                                            <TableCell className="font-medium">{s.supplier_name}</TableCell>
                                            <TableCell className="text-muted-foreground">{s.region ?? '—'}</TableCell>
                                            <TableCell>{s.total_procurements}</TableCell>
                                            <TableCell>
                                                <span className={`font-semibold ${rateColor(s.rejection_rate)}`}>
                                                    {s.rejection_rate.toFixed(1)}%
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <span className={`font-semibold ${rateColor(s.completion_rate, true)}`}>
                                                    {s.completion_rate.toFixed(1)}%
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-16">
                                                        <Progress value={s.kg_contribution_pct} className="h-1.5" />
                                                    </div>
                                                    <span className="text-xs font-semibold text-primary">
                                                        {s.kg_contribution_pct.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-semibold text-red-600">
                                                {s.total_net_damage_kg > 0 ? formatKg(s.total_net_damage_kg) : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`font-semibold ${
                                                    s.net_damage_pct > 10 ? 'text-red-600' : 
                                                    s.net_damage_pct > 5 ? 'text-yellow-600' : 
                                                    'text-green-600'
                                                }`}>
                                                    {s.net_damage_pct.toFixed(2)}%
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                                                {formatDate(s.latest_order_date)}
                                            </TableCell>
                                            <TableCell>{riskBadge(s.risk_level)}</TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* ── Sourcing Performance Table ── */}
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-semibold">Sourcing Performance</CardTitle>
                        <p className="text-xs text-muted-foreground">All suppliers · Approved records only within date range · Click column to sort</p>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <SortTh label="Supplier" field="supplier_name" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <TableHead>Categories</TableHead>
                                    <SortTh label="Approved Value" field="total_value" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="Approved KG" field="approved_quantity_kg" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="KG Share" field="kg_contribution_pct" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="Gross Damage (kg)" field="total_gross_damage_kg" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="Gross %" field="gross_damage_pct" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="Compensation (kg)" field="total_compensation_kg" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="Comp %" field="compensation_pct" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="Net Damage (kg)" field="total_net_damage_kg" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <SortTh label="Net %" field="net_damage_pct" current={perfSort} dir={perfDir}
                                        onClick={(f) => handleSort(f, perfSort, setPerfSort, perfDir, setPerfDir)} />
                                    <TableHead>Pending</TableHead>
                                    <TableHead>Completed</TableHead>
                                    <TableHead>Rejected</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? <TableSkeletonRows cols={15} /> :
                                    sortedPerf.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={15} className="text-center text-muted-foreground py-10">
                                                No supplier records found in the selected date range.
                                            </TableCell>
                                        </TableRow>
                                    ) : sortedPerf.map((s) => (
                                        <TableRow key={s.supplier_id} className="hover:bg-muted/30">
                                            <TableCell className="font-medium">{s.supplier_name}</TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-1 max-w-[160px]">
                                                    {s.categories.length > 0 ? (
                                                        s.categories.slice(0, 2).map((c) => (
                                                            <Badge key={c} variant="outline" className="text-xs py-0">{c}</Badge>
                                                        ))
                                                    ) : (
                                                        <span className="text-xs text-muted-foreground">—</span>
                                                    )}
                                                    {s.categories.length > 2 && (
                                                        <Badge variant="outline" className="text-xs py-0">+{s.categories.length - 2}</Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-medium">
                                                {s.total_value > 0 ? formatCurrency(s.total_value) : '—'}
                                            </TableCell>
                                            <TableCell className="font-medium text-primary">
                                                {s.approved_quantity_kg > 0 ? formatKg(s.approved_quantity_kg) : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <div className="w-12">
                                                        <Progress value={s.kg_contribution_pct} className="h-1.5" />
                                                    </div>
                                                    <span className="text-xs font-semibold text-primary whitespace-nowrap">
                                                        {s.kg_contribution_pct.toFixed(1)}%
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell className="font-medium text-orange-600">
                                                {s.total_gross_damage_kg > 0 ? formatKg(s.total_gross_damage_kg) : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`font-semibold ${
                                                    s.gross_damage_pct > 10 ? 'text-red-600' : 
                                                    s.gross_damage_pct > 5 ? 'text-yellow-600' : 
                                                    'text-green-600'
                                                }`}>
                                                    {s.gross_damage_pct.toFixed(2)}%
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-green-600">
                                                {s.total_compensation_kg > 0 ? formatKg(s.total_compensation_kg) : '—'}
                                            </TableCell>
                                            <TableCell className="text-green-600 font-medium">
                                                {s.compensation_pct.toFixed(2)}%
                                            </TableCell>
                                            <TableCell className="font-semibold text-red-600">
                                                {s.total_net_damage_kg > 0 ? formatKg(s.total_net_damage_kg) : '—'}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`font-semibold ${
                                                    s.net_damage_pct > 10 ? 'text-red-600' : 
                                                    s.net_damage_pct > 5 ? 'text-yellow-600' : 
                                                    'text-green-600'
                                                }`}>
                                                    {s.net_damage_pct.toFixed(2)}%
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {s.pending_procurements > 0 ? (
                                                    <span className="inline-flex items-center gap-1 text-yellow-700 text-xs font-medium">
                                                        <Clock className="h-3 w-3" />{s.pending_procurements}
                                                    </span>
                                                ) : <span className="text-muted-foreground">0</span>}
                                            </TableCell>
                                            <TableCell>
                                                <span className="inline-flex items-center gap-1 text-green-700 text-xs font-medium">
                                                    <CheckCircle2 className="h-3 w-3" />{s.completed_procurements}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {s.rejected_procurements > 0 ? (
                                                    <span className="inline-flex items-center gap-1 text-red-700 text-xs font-medium">
                                                        <XCircle className="h-3 w-3" />{s.rejected_procurements}
                                                    </span>
                                                ) : <span className="text-muted-foreground">0</span>}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant={s.status === 'active' ? 'default' : 'secondary'} className="text-xs capitalize">
                                                    {s.status}
                                                </Badge>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                <p className="text-xs text-muted-foreground text-right pb-2">
                    KPIs computed client-side from approved procurement records within the selected date range.
                </p>
            </div>
        </Layout>
    );
};

export default SupplierGovernanceDashboard;
